import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { filesize } from "src/ui/utils/filesize";
import { makeStyles } from "tss-react/mui";

import apiClient from "@dashboard/files/api/apiClient";
import { Box, Card, CardContent, Theme, Typography } from "@mui/material";
import ApmLink from "@shared/ApmLink";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { PageContentWrapper } from "@shared/PageContentWrapper/PageContentWrapper";
import { showErrorToast } from "@shared/toasts/Toasts";

import { EnrollmentAndroidOsStepper } from "./EnrollmentAndroidOsStepper";

const useStyles = makeStyles()((theme: Theme) => ({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    container: {
        width: "100%",
        borderRadius: theme.spacing(2),
    },
    content: {
        flex: "0 0 auto",
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(2),
    },
    stepper: {
        marginBottom: theme.spacing(1),
    },
    downloadSection: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        width: "100%",
    },
    downloadBox: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(4),
        backgroundColor: theme.palette.emteria.background.paper,
        border: `2px dashed ${theme.palette.emteria.border.primary}`,
        borderRadius: theme.spacing(1.5),
        width: "100%",
    },
    productName: {
        fontWeight: 600,
        color: theme.palette.emteria.text.primary,
    },
    metaText: {
        color: theme.palette.emteria.text.secondary,
    },
    downloadButton: {
        minWidth: 160,
        marginTop: theme.spacing(2),
    },
    tutorialAlert: {
        width: "100%",
    },
    tutorialList: {
        listStyleType: "disc",
        marginLeft: theme.spacing(6),
        marginTop: theme.spacing(2),
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface DownloadImageStepProps {
    onSkip: () => void;
    onBack?: () => void;
    onContinue: () => void;
    selectedReleaseFile: API.ReleaseFile | null;
}

export const DownloadImageStep: React.FC<DownloadImageStepProps> = ({ onSkip, onBack, onContinue, selectedReleaseFile }) => {
    const [loading, setLoading] = useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    const handleDownload = async () => {
        if (!selectedReleaseFile) {
            return;
        }
        try {
            setLoading(true);
            await apiClient.invokeFileDownload(selectedReleaseFile.id, selectedReleaseFile.name ?? "", true);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContentWrapper size="wide" className={classes.wrapper}>
            <EnrollmentAndroidOsStepper activeStep={1} className={classes.stepper} />
            <Card className={classes.container} elevation={3}>
                <CardContent className={classes.content}>
                    <Box className={classes.titleContainer}>
                        <Typography variant="h6" className={classes.title}>
                            {t("modals:enrollmentDialog.downloadFlashStep.title")}
                        </Typography>
                    </Box>
                    <Typography variant="body1" className={classes.description}>
                        {t("modals:enrollmentDialog.downloadFlashStep.description")}
                    </Typography>

                    <Box className={classes.downloadSection}>
                        <Box className={classes.downloadBox}>
                            {selectedReleaseFile ? (
                                <>
                                    <Typography variant="subtitle1" className={classes.productName}>
                                        {selectedReleaseFile.productName}
                                    </Typography>
                                    <Typography variant="body2" className={classes.metaText}>
                                        {t("common:label.version")}: {selectedReleaseFile.version}
                                    </Typography>
                                    <Typography variant="body2" className={classes.metaText}>
                                        {t("modals:enrollmentDialog.downloadFlashStep.size")}:{" "}
                                        {filesize(selectedReleaseFile.size)}
                                    </Typography>
                                    <PrimaryButton
                                        className={classes.downloadButton}
                                        onClick={handleDownload}
                                        loading={loading}
                                        disabled={loading}
                                        startIcon={
                                            <Box component="span">
                                                <i className="fa-solid fa-download" />
                                            </Box>
                                        }
                                    >
                                        {t("modals:enrollmentDialog.downloadFlashStep.buttons.download")}
                                    </PrimaryButton>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle1" className={classes.productName}>
                                        {t("modals:enrollmentDialog.downloadFlashStep.deviceTitle")}
                                    </Typography>
                                    <Typography variant="body2" className={classes.metaText}>
                                        {t("modals:enrollmentDialog.downloadFlashStep.deviceVersion")}
                                    </Typography>
                                    <PrimaryButton
                                        className={classes.downloadButton}
                                        disabled
                                        startIcon={
                                            <Box component="span">
                                                <i className="fa-solid fa-download" />
                                            </Box>
                                        }
                                    >
                                        {t("modals:enrollmentDialog.downloadFlashStep.buttons.download")}
                                    </PrimaryButton>
                                </>
                            )}
                        </Box>

                        <CustomAlert severity="info" className={classes.tutorialAlert}>
                            <Typography>{t("modals:enrollmentDialog.downloadFlashStep.tutorial.title")}</Typography>
                            <ul className={classes.tutorialList}>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            title={t("modals:enrollmentDialog.downloadFlashStep.tutorial.items.configuration")}
                                            href="https://emteria.com/kb/initial-configuration-setup-wizard"
                                        >
                                            {t("modals:enrollmentDialog.downloadFlashStep.tutorial.items.configuration")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            title={t("modals:enrollmentDialog.downloadFlashStep.tutorial.items.management")}
                                            href="https://emteria.com/kb/enable-mdm"
                                        >
                                            {t("modals:enrollmentDialog.downloadFlashStep.tutorial.items.management")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                            </ul>
                        </CustomAlert>
                    </Box>
                </CardContent>
                <Box className={classes.actions}>
                    <Box className={classes.actionsLeft}>
                        <SecondaryButton onClick={onBack || onSkip}>{t("common:button.goBack")}</SecondaryButton>
                    </Box>
                    <PrimaryButton onClick={onContinue}>{t("common:button.continue")}</PrimaryButton>
                </Box>
            </Card>
        </PageContentWrapper>
    );
};
